/**
 * Academy (College) API Client
 *
 * Wraps all /discourse/api/v1/college endpoints.
 */

import { apiFetch } from "@/lib/api/client"
import type {
  CourseSeriesListResp,
  CourseSeries,
  CollegeCourseListResp,
  CollegeCourseItem,
  CollegeChapterListResp,
  CollegeStepListResp,
  CollegeCompleteChapterResp,
} from "@zrt-ai-office/types/course"

const D = "/discourse/api/v1/college"

// ── Series ──

export async function fetchSeriesList(
  page = 1,
  pageSize = 20,
): Promise<CourseSeriesListResp> {
  return apiFetch<CourseSeriesListResp>(`${D}/series/list`, {
    params: { page, pageSize },
  })
}

export async function fetchSeriesDetail(id: string | number): Promise<CourseSeries> {
  return apiFetch<CourseSeries>(`${D}/series/${id}`)
}

export async function fetchSeriesCourses(id: string | number): Promise<CollegeCourseListResp> {
  return apiFetch<CollegeCourseListResp>(`${D}/series/${id}/courses`)
}

// ── Course ──

export async function fetchCourseList(
  seriesId?: number,
  page = 1,
  pageSize = 20,
): Promise<CollegeCourseListResp> {
  return apiFetch<CollegeCourseListResp>(`${D}/course/list`, {
    params: {
      seriesId: seriesId !== undefined ? seriesId : undefined,
      page,
      pageSize,
    },
  })
}

export async function fetchCourseDetail(id: string | number): Promise<CollegeCourseItem> {
  return apiFetch<CollegeCourseItem>(`${D}/course/${id}`)
}

// ── Chapter ──

export async function fetchCourseChapters(
  courseId: string | number,
): Promise<CollegeChapterListResp> {
  return apiFetch<CollegeChapterListResp>(
    `${D}/course/${courseId}/chapters`,
  )
}

// ── Step ──

export async function fetchChapterSteps(
  chapterId: string | number,
  courseId: string | number,
): Promise<CollegeStepListResp> {
  return apiFetch<CollegeStepListResp>(
    `${D}/chapter/${chapterId}/steps`,
    { params: { courseId } },
  )
}

// ── Actions ──

export async function completeChapter(
  chapterId: string | number,
  courseId: string,
): Promise<CollegeCompleteChapterResp> {
  return apiFetch<CollegeCompleteChapterResp>(
    `${D}/chapter/${chapterId}/complete`,
    { method: "POST", body: { courseId: Number(courseId) } },
  )
}

export async function uncompleteChapter(
  chapterId: string | number,
  courseId: string,
): Promise<CollegeCompleteChapterResp> {
  return apiFetch<CollegeCompleteChapterResp>(
    `${D}/chapter/${chapterId}/uncomplete`,
    { method: "POST", body: { courseId: Number(courseId) } },
  )
}
